import * as semaphore from 'semaphore';
import { OfflineStorageObject } from './offline-storage-object';
import { OfflineStorageTimestampService } from '../../offline-storage-timestamp.service';
import { ExpiredOfflineObjectRemover } from './expired-offline-object-remover';
import { NgForage } from 'ngforage';
import SemaphoreHelper from '../../../helpers/semaphore-helper';
import { CollectionHelper } from 'src/app/helpers/collection-helper';

// TODO refactor PlacerSubProjectChangesService etc. to extend this class
// TODO replace all usages of this class by AbstractOfflineStorageAsyncTimestampService
export abstract class AbstractOfflineStorageService<
  ID,
  O extends OfflineStorageObject<ID>,
  R extends ExpiredOfflineObjectRemover<ID, O>
> {
  public readonly offlineStorageMutex = semaphore(1);

  private remover: R;

  constructor(
    public readonly storageKeyPrefix: string,
    public readonly timestampCollectionKey: string,
    protected offlineStorageTimestampService: OfflineStorageTimestampService,
    protected ngForage: NgForage
  ) {
    this.remover = this.instantiateRemover();
    this.remover.removeExpiredItems();
  }

  protected abstract instantiateRemover(): R;

  public getObjectStorageKey(id: ID): string {
    if (id == null) {
      throw new Error('id must not be null');
    }
    return this.storageKeyPrefix + this.stringifyID(id);
  }

  public abstract convertToID(o: any): ID;

  protected abstract stringifyID(id: ID): string;

  public async getObject(id: ID): Promise<O> {
    const object = await this.ngForage.getItem<O>(this.getObjectStorageKey(id));
    if (object != null) {
      SemaphoreHelper.use(this.offlineStorageMutex, async () => {
        this.offlineStorageTimestampService.setTimestamps(
          this.timestampCollectionKey,
          [{ key: id.toString(), timestamp: Date.now() }]
        );
      });
    }
    return this.processRetrievedObject(object);
  }

  public async getObjects(ids: ID[]): Promise<O[]> {
    if (ids == null) {
      return null;
    }
    if (ids.length === 0) {
      return [];
    }

    return this.getObjectsPromise(ids).then((objects) =>
      this.postProcessObjects(objects)
    );
  }

  private async getObjectsPromise(ids: ID[]): Promise<O[]> {
    return await Promise.all(ids.map((id) => this.getObjectOrNull(id)));
  }

  private postProcessObjects(objects: O[]): O[] {
    CollectionHelper.filterInPlace(objects, (o) => o != null);

    if (CollectionHelper.isPopulated(objects)) {
      SemaphoreHelper.use(this.offlineStorageMutex, async () => {
        const now = Date.now();
        this.offlineStorageTimestampService.setTimestamps(
          this.timestampCollectionKey,
          objects.map((o) => {
            return {
              key: o.id.toString(),
              timestamp: now,
            };
          })
        );
      });
    }

    for (let i = 0; i < objects.length; i++) {
      objects[i] = this.processRetrievedObject(objects[i]);
    }

    return objects;
  }

  private async getObjectOrNull(id: ID): Promise<O> {
    return id == null
      ? null
      : await this.ngForage.getItem<O>(this.getObjectStorageKey(id));
  }

  protected processRetrievedObject(object: O): O {
    return object;
  }

  public async storeObject(object: O): Promise<void> {
    this.verifyToStoreObject(object);
    object = this.preprocessToStoreObject(object);
    await SemaphoreHelper.use(this.offlineStorageMutex, async () => {
      await this.ngForage.setItem(this.getObjectStorageKey(object.id), object);
      this.offlineStorageTimestampService.setTimestamps(
        this.timestampCollectionKey,
        [
          {
            key: object.id.toString(),
            timestamp: Date.now(),
          },
        ]
      );
    });
  }

  protected abstract verifyToStoreObject(object: O): void;

  protected preprocessToStoreObject(object: O): O {
    return object;
  }

  public async removeObject(object: O): Promise<void> {
    if (object == null) {
      return;
    }
    SemaphoreHelper.use(this.offlineStorageMutex, async () => {
      const storageKey = this.getObjectStorageKey(object.id);
      const toRemove = await this.ngForage.getItem<O>(storageKey);
      if (toRemove != null) {
        this.cleanupOfflineObject(toRemove);
      }
      await this.ngForage.removeItem(storageKey);
      this.offlineStorageTimestampService.removeItems(
        this.timestampCollectionKey,
        [this.stringifyID(object.id)]
      );
    });
  }

  public abstract cleanupOfflineObject(object: O): Promise<void>;
}
